import { useState, useEffect, useCallback } from "react";

import { useNavigate } from "react-router-dom";

import { useConfig } from './configContext.js';
import PropTypes from 'prop-types';


import { AuthContext } from './authContext.js';

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loadingInitial, setLoadingInitial] = useState(true);

    const { apiUrl, authMethod } = useConfig();
    const navigate = useNavigate();

    const useCAS = authMethod.toLowerCase() === 'cas';

    // Memoised so the mount effect can list it as a dependency without
    // re-running on every render. Reads window.location directly: it needs the
    // URL at the moment of the call, which is exactly what the browser holds.
    const loginCas = useCallback(() => {
        const queryParameters = new URLSearchParams(window.location.search);

        // No fetch here, and so no AbortController: the navigation below hands
        // the browser over to the CAS endpoint.
        setLoadingInitial(true);
        const url = queryParameters.has('ticket') ? `${apiUrl}/login?ticket=${queryParameters.get('ticket')}` : `${apiUrl}/login`;

        window.location.href = url;
    }, [apiUrl]);

    // Check if there is a currently active session
    // when the provider is mounted for the first time.
    //
    // If there is an error, it means there is no session.
    //
    // Finally, just signal the component that the initial load
    // is over.
    useEffect(() => {
        fetch(`${apiUrl}/session`, { credentials: 'include' })
            .then(async (response) => {
                if (response.ok) {
                    const user = await response.json();
                    setUser(user);
                } if (response?.status === 401) {
                    if (useCAS) {
                        loginCas();
                    }
                    else {
                        navigate('/login');
                    }
                }
                else if (!response.ok) {
                    throw new Error();
                }
            })
            .catch(() => {
                if (useCAS) {
                    loginCas();
                }
            })
            .finally(() => setLoadingInitial(false));
    }, [apiUrl, useCAS, navigate, loginCas]);


    const login = async(email, password) => {
        if (useCAS) {
            loginCas();
            return Promise.reject(new Error("Authentification CAS en cours."));
        } else {
           return await loginForm(email, password);
        }
    };
    async function loginForm(email, password) {
        const timeout = 8000

        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);

        return fetch(`${apiUrl}/login`, {
            credentials: 'include',
            method: "POST",
            body: JSON.stringify({ login: email, password: password }),
            headers: {
                'Content-Type': 'application/json'
            },
            signal: controller.signal
        })
            .then(async (response) => {
                const data = await response.json();
                if (response.ok) {
                    setUser(data);
                    return {
                        success: true
                    };
                }
                if (response?.status === 401) {
                    return { message: "L'authentification a échoué." };
                }
                return { message: "Une erreur est survenue." };
            })
            // Same shape as the other outcomes: the caller reads
            // `result.message`, and an undefined result would throw.
            .catch(() => ({ message: "Une erreur est survenue." }))
            .finally(() => {
                clearTimeout(id);
            });


    }



    function logoutCas() {
        window.location.href = `${apiUrl}/logout`;
    }
    function simpleLogout() {
        fetch(`${apiUrl}/logout`, { credentials: 'include' })
            .then(() => {
                setUser(undefined);
                navigate("/login");
            }
            );
    }

    // Call the logout endpoint and then remove the user from the state.
    function logout() {
        setLoadingInitial(true);
        if (useCAS) {
            logoutCas();
        } else {
            simpleLogout();
        }
        setLoadingInitial(false);
    }

    const value = {
        authMethod,
        user,
        loadingInitial,
        login,
        // Login calls this when it lands with no session under CAS; without it
        // in the context the call is a ReferenceError.
        loginCas,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loadingInitial && children}
        </AuthContext.Provider>
    );
}

AuthProvider.propTypes = {
    children: PropTypes.node,
};

